/**
 * TaskTagPicker — a CONTROLLED multi-select tag picker for the task create/edit
 * form. Unlike the clients TagPicker (which mutates assignments immediately),
 * selection here is local `value`/`onChange` state and is persisted only when the
 * form saves (the task POST/PATCH carries `tagIds`). It reuses the SHARED tenant
 * tag vocabulary (`/api/tags`, queryKey ["tags"]) and the clients TagPill +
 * TAG_COLORS, so tasks and clients draw from one set of tags.
 */

import { useState, useRef, useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, Input } from "@provenleap/ui";
import { Plus, Check, MagnifyingGlass, Tag } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "motion/react";
import { apiGet, apiPost } from "../../lib/apiClient";
import { TagPill } from "../clients/TagPill";
import { TAG_COLORS } from "../clients/types";
import type { TenantTag } from "../clients/types";
import type { TaskTag } from "./types";

interface TaskTagPickerProps {
  value: TaskTag[];
  onChange: (tags: TaskTag[]) => void;
  onToast?: (type: "success" | "error" | "info", message: string) => void;
}

const MAX_TAGS = 20;

export function TaskTagPicker({ value, onChange, onToast }: TaskTagPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newTagName, setNewTagName] = useState("");
  const [newTagColor, setNewTagColor] = useState<string>(TAG_COLORS[0]!.value);
  const popoverRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  const selectedIds = new Set(value.map((t) => t.id));
  const isAtLimit = value.length >= MAX_TAGS;

  useEffect(() => {
    if (!isOpen) return;
    function handleClickOutside(e: MouseEvent) {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setShowCreateForm(false);
        setSearch("");
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const { data: allTags = [] } = useQuery<TenantTag[]>({
    queryKey: ["tags"],
    queryFn: async () => {
      const res = await apiGet("/api/tags");
      if (!res.ok) throw new Error("Failed to load tags");
      const json = await res.json();
      return json.data;
    },
    enabled: isOpen,
  });

  const filteredTags = useMemo(
    () =>
      allTags
        .filter((tag) => tag.name.toLowerCase().includes(search.toLowerCase()))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [allTags, search],
  );

  const createTag = useMutation({
    mutationFn: async ({ name, color }: { name: string; color: string }) => {
      const res = await apiPost("/api/tags", { name, color });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw { status: res.status, ...err };
      }
      const json = await res.json();
      return json.data as TenantTag;
    },
    onSuccess: (newTag) => {
      queryClient.invalidateQueries({ queryKey: ["tags"] });
      // Select the freshly created tag (local — persisted when the form saves).
      if (value.length < MAX_TAGS) {
        onChange([
          ...value,
          { id: newTag.id, name: newTag.name, color: newTag.color },
        ]);
      }
      setNewTagName("");
      setNewTagColor(TAG_COLORS[0]!.value);
      setShowCreateForm(false);
    },
    onError: (err: { type?: string; title?: string }) => {
      if (err?.type?.includes?.("duplicate-tag")) {
        onToast?.("error", "A tag with this name already exists");
      } else {
        onToast?.("error", err?.title ?? "Failed to create tag");
      }
    },
  });

  function toggleTag(tag: TenantTag) {
    if (selectedIds.has(tag.id)) {
      onChange(value.filter((t) => t.id !== tag.id));
    } else if (!isAtLimit) {
      onChange([...value, { id: tag.id, name: tag.name, color: tag.color }]);
    }
  }

  function handleCreateTag() {
    const trimmed = newTagName.trim();
    if (!trimmed || isAtLimit) return;
    createTag.mutate({ name: trimmed, color: newTagColor });
  }

  return (
    <div className="space-y-2">
      {/* Selected tags as removable pills */}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="task-tag-selection">
          {value.map((tag) => (
            <TagPill
              key={tag.id}
              tag={tag}
              onRemove={() => onChange(value.filter((t) => t.id !== tag.id))}
            />
          ))}
        </div>
      )}

      <div className="relative" ref={popoverRef}>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="cursor-pointer rounded-lg border-[0.5px] shadow-xs hover:-translate-y-0.5 hover:shadow-sm hover:bg-background transition-all"
          style={{ border: "0.5px solid var(--hairline)" }}
          onClick={() => setIsOpen(!isOpen)}
          data-testid="task-tag-picker-trigger"
        >
          <Tag size={14} className="mr-1" />
          Add Tag
        </Button>

        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: -4 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -4 }}
              transition={{ duration: 0.15, ease: "easeOut" }}
              className="absolute left-0 top-full mt-1 z-[60] w-72 rounded-xl bg-popover shadow-md overflow-hidden"
              style={{ border: "0.5px solid var(--hairline)" }}
              data-testid="task-tag-picker-popover"
            >
              <div
                className="p-2"
                style={{ borderBottom: "0.5px solid hsl(var(--input) / 0.4)" }}
              >
                <div className="relative">
                  <MagnifyingGlass
                    size={14}
                    className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground"
                  />
                  <Input
                    placeholder="Search tags..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-7 h-8 text-sm border-0 shadow-none focus-visible:ring-0"
                    data-testid="task-tag-search-input"
                  />
                </div>
              </div>

              <div className="max-h-48 overflow-y-auto p-2">
                {filteredTags.length === 0 && !showCreateForm && (
                  <p className="text-sm text-muted-foreground text-center py-3">
                    No tags found
                  </p>
                )}
                {filteredTags.map((tag) => {
                  const isSelected = selectedIds.has(tag.id);
                  const isDisabled = !isSelected && isAtLimit;
                  return (
                    <button
                      key={tag.id}
                      type="button"
                      onClick={() => toggleTag(tag)}
                      disabled={isDisabled}
                      className={`w-full flex items-center gap-2 rounded-lg px-2 py-1.5 text-[13px] hover:bg-accent ${
                        isDisabled
                          ? "opacity-50 cursor-not-allowed"
                          : "cursor-pointer"
                      }`}
                      title={isDisabled ? "Tag limit reached (20 max)" : undefined}
                      data-testid="task-tag-picker-item"
                    >
                      <span
                        className="h-3 w-3 rounded-full shrink-0"
                        style={{ backgroundColor: tag.color }}
                      />
                      <span className="flex-1 text-left truncate">
                        {tag.name}
                      </span>
                      {isSelected && (
                        <Check size={14} className="text-primary shrink-0" />
                      )}
                    </button>
                  );
                })}
              </div>

              <div
                className="p-2"
                style={{ borderTop: "0.5px solid hsl(var(--input) / 0.4)" }}
              >
                {!showCreateForm ? (
                  <button
                    type="button"
                    onClick={() => setShowCreateForm(true)}
                    disabled={isAtLimit}
                    title={
                      isAtLimit ? "Tag limit reached (20 max)" : undefined
                    }
                    className={`w-full flex items-center gap-2 rounded-lg px-2 py-1.5 text-[13px] ${
                      isAtLimit
                        ? "opacity-50 cursor-not-allowed"
                        : "hover:bg-accent cursor-pointer"
                    }`}
                    data-testid="task-create-tag-button"
                  >
                    <Plus size={14} />
                    Create New Tag
                  </button>
                ) : (
                  <div className="space-y-2">
                    <Input
                      placeholder="Tag name"
                      value={newTagName}
                      onChange={(e) => setNewTagName(e.target.value)}
                      maxLength={50}
                      className="h-8 text-sm"
                      data-testid="task-new-tag-name-input"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleCreateTag();
                        if (e.key === "Escape") setShowCreateForm(false);
                      }}
                    />
                    <div className="grid grid-cols-6 gap-1">
                      {TAG_COLORS.map((c) => (
                        <button
                          key={c.value}
                          type="button"
                          onClick={() => setNewTagColor(c.value)}
                          className={`h-7 w-7 rounded-full border-2 ${
                            newTagColor === c.value
                              ? "border-foreground"
                              : "border-transparent"
                          }`}
                          style={{
                            backgroundColor: c.value,
                            minWidth: 0,
                            minHeight: 0,
                          }}
                          title={c.label}
                          aria-label={`Select ${c.label} color`}
                        />
                      ))}
                    </div>
                    <div className="flex gap-1.5 justify-center">
                      <Button
                        type="button"
                        size="sm"
                        className="h-9 text-xs rounded-xl cursor-pointer"
                        onClick={handleCreateTag}
                        disabled={!newTagName.trim() || createTag.isPending}
                        data-testid="task-confirm-create-tag"
                      >
                        {createTag.isPending ? "Creating..." : "Create"}
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-9 text-xs rounded-xl cursor-pointer"
                        onClick={() => {
                          setShowCreateForm(false);
                          setNewTagName("");
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
